import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './styles/ChatBox.css';

const TypingIndicator = () => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 4,
    padding: '10px 16px', maxWidth: 'fit-content',
    backgroundColor: '#f0f0f0', borderRadius: '18px 18px 18px 4px',
    marginLeft: 8,
  }}>
    <div className="typing__dot" />
    <div className="typing__dot" />
    <div className="typing__dot" />
  </div>
);

const ChatBox = ({ setOpenChat, isMobileView }) => {
  const [isTyping, setIsTyping] = useState(false);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const responses = {
    'Leveranstid & frakt': {
      text: 'Vi skickar alla ordrar inom 24 timmar! Leveransen tar sedan 1–3 arbetsdagar. Frakt är gratis på ordrar över 349 kr, annars kostar den 49 kr.',
      followUp: ['Kan jag spåra min order?', 'Hur returnerar jag en poster?'],
    },
    'Vilka posters har ni?': {
      text: 'Vi erbjuder ett brett utbud av posters i flera kategorier: Djur, Illustrationer, Abstrakt, Natur, Fotokonst och Framtid. Alla finns i storlekarna 21x30, 30x40 och 50x70 cm.',
      followUp: ['Vilka storlekar finns?', 'Har ni ramar också?'],
    },
    'Hjälp mig välja poster': {
      text: 'Självklart! Berätta lite om din stil — gillar du färgglatt och lekfullt, eller mer minimalistiskt? Ska postern hänga i vardagsrum, sovrum eller kontor? Jag hjälper dig hitta rätt!',
      followUp: [],
    },
    'Storlekar & priser': {
      text: 'Våra posters finns i tre storlekar:\n• 21×30 cm — från 149 kr\n• 30×40 cm — från 199 kr\n• 50×70 cm — från 279 kr\nAlla posters trycks på 200g premium matt papper.',
      followUp: ['Har ni ramar också?', 'Leveranstid & frakt'],
    },
    'Kan jag spåra min order?': {
      text: 'Ja! Du får ett spårningsnummer via e-post så snart din order har skickats. Med det kan du följa paketet hela vägen hem.',
      followUp: ['Leveranstid & frakt', 'Hur returnerar jag en poster?'],
    },
    'Hur returnerar jag en poster?': {
      text: 'Du har 14 dagars öppet köp från leveransdagen. Postern ska vara i originalskick. Kontakta oss så skickar vi en returetikett — det är helt kostnadsfritt!',
      followUp: ['Leveranstid & frakt', 'Storlekar & priser'],
    },
    'Vilka storlekar finns?': {
      text: 'Vi erbjuder tre storlekar på alla posters:\n• 21×30 cm (A4) — perfekt för hyllor och mindre väggar\n• 30×40 cm — vår mest populära storlek\n• 50×70 cm — för den som vill göra ett statement!',
      followUp: ['Storlekar & priser', 'Har ni ramar också?'],
    },
    'Har ni ramar också?': {
      text: 'Just nu säljer vi enbart posters utan ram. Men våra storlekar passar perfekt i standardramar från IKEA och andra inredningsbutiker!',
      followUp: ['Vilka storlekar finns?', 'Vilka posters har ni?'],
    },
  };

  const initialButtons = ['Leveranstid & frakt', 'Vilka posters har ni?', 'Hjälp mig välja poster', 'Storlekar & priser'];

  const [messages, setMessages] = useState([
    {
      sender: 'ai',
      text: 'Hej! Välkommen till PosterTopia. Hur kan jag hjälpa dig?',
      buttons: initialButtons,
    },
  ]);

  const handleButtonClick = (option) => {
    setIsTyping(true);
    const userMessage = { sender: 'user', text: option };
    setMessages((prev) => {
      const updated = [...prev, userMessage];
      handleSubmit(updated);
      return updated;
    });

    const match = responses[option];
    if (match) {
      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          {
            sender: 'ai',
            text: match.text,
            ...(match.followUp && match.followUp.length > 0 ? { buttons: match.followUp } : {}),
          },
        ]);
        setIsTyping(false);
      }, 1200);
    }
  };

  const handleSubmit = async (conversation) => {
    try {
      const response = await axios.post('http://127.0.0.1:8000/support', {
        conversation,
      });
      setMessages((prev) => [...prev, { sender: 'ai', text: response.data.response }]);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleMessageSend = () => {
    if (!input.trim()) return;
    const userMessage = { sender: 'user', text: input };
    setMessages((prev) => {
      const updated = [...prev, userMessage];
      handleSubmit(updated);
      return updated;
    });
    setInput('');
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: isMobileView ? 8 : 16,
      right: isMobileView ? 8 : 16,
      width: isMobileView ? 'calc(100vw - 16px)' : '380px',
      maxHeight: isMobileView ? '80vh' : '560px',
      background: '#fff',
      borderRadius: 20,
      boxShadow: '0 12px 48px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      zIndex: 9999,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      {/* Header */}
      <div style={{
        background: '#161825',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexShrink: 0,
      }}>
        {/* AI icon */}
        <div style={{
          width: 40, height: 40, borderRadius: 12,
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2a4 4 0 0 1 4 4v1a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z" />
            <path d="M6 10a2 2 0 0 0-2 2v1a2 2 0 0 0 2 2" />
            <path d="M18 10a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2" />
            <path d="M12 16v4" />
            <path d="M8 22h8" />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#fff', fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>
            PosterTopia Support
          </div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 1 }}>
            AI-assistent
          </div>
        </div>
        <button
          onClick={() => setOpenChat(false)}
          style={{
            width: 32, height: 32, borderRadius: 8,
            border: 'none', background: 'rgba(255,255,255,0.1)',
            color: '#fff', cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s',
          }}
          onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
          onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '16px 16px 8px',
        display: 'flex', flexDirection: 'column', gap: 12,
        minHeight: isMobileView ? '35vh' : '340px',
        maxHeight: isMobileView ? '50vh' : '380px',
        background: '#fafafa',
      }}>
        {messages.map((message, index) => {
          const isAI = message.sender === 'ai';
          return (
            <div key={index} style={{
              display: 'flex', flexDirection: 'column',
              alignItems: isAI ? 'flex-start' : 'flex-end',
            }}>
              {/* Sender label */}
              <div style={{
                fontSize: 11, fontWeight: 600, color: '#999',
                marginBottom: 4,
                paddingLeft: isAI ? 4 : 0,
                paddingRight: isAI ? 0 : 4,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                {isAI ? 'AI' : 'Du'}
              </div>

              {/* Bubble */}
              <div style={{
                padding: '10px 16px',
                borderRadius: isAI ? '18px 18px 18px 4px' : '18px 18px 4px 18px',
                backgroundColor: isAI ? '#f0f0f0' : '#161825',
                color: isAI ? '#1a1a1a' : '#fff',
                maxWidth: '85%',
                fontSize: 14,
                lineHeight: 1.55,
                wordWrap: 'break-word',
              }}>
                {message.text}
              </div>

              {/* Quick-reply buttons */}
              {isAI && message.buttons && (
                <div style={{
                  display: 'flex', flexWrap: 'wrap', gap: 6,
                  marginTop: 8, maxWidth: '90%',
                }}>
                  {message.buttons.map((btn, i) => (
                    <button
                      key={i}
                      onClick={() => handleButtonClick(btn)}
                      style={{
                        padding: '7px 14px',
                        borderRadius: 20,
                        border: '1.5px solid #e0e0e0',
                        background: '#fff',
                        color: '#333',
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        lineHeight: 1.3,
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.borderColor = '#161825';
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = '#e0e0e0';
                        e.currentTarget.style.backgroundColor = '#fff';
                      }}
                    >
                      {btn}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {isTyping && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: '#fff',
        flexShrink: 0,
      }}>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleMessageSend(); }}
          placeholder="Skriv ett meddelande..."
          style={{
            flex: 1,
            height: 40,
            padding: '0 14px',
            borderRadius: 12,
            border: '1.5px solid #e5e5e5',
            background: '#f8f8f8',
            fontSize: 14,
            outline: 'none',
            transition: 'border-color 0.15s',
            fontFamily: 'inherit',
          }}
          onFocus={(e) => e.currentTarget.style.borderColor = '#161825'}
          onBlur={(e) => e.currentTarget.style.borderColor = '#e5e5e5'}
        />
        <button
          onClick={handleMessageSend}
          style={{
            width: 40, height: 40, borderRadius: 12,
            border: 'none',
            background: input.trim() ? '#161825' : '#e0e0e0',
            color: '#fff',
            cursor: input.trim() ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            transition: 'background 0.15s',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ChatBox;
